import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../../../components/axiosInstance";
import { API_URLS } from "../../../../apiUrl";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../../../../components/CustomSelect";

type CollectorType = "" | "counter" | "field";

interface Borough { id: number; borough_name: any; borough_primary_id: any; }
interface Ward { id: number; name: string; }
interface RoleType { primary_role_id: number; role_name: string; status: number; }

interface FormData {
  login_user_name: string;
  admin_name: string;
  municipality_name: string;
  password: string;
  email: string;
  role_id: string;
  ward_id: string;
  municipality_id: any;
}

const MunicipalityID = localStorage.getItem("municipalityId");
const municipalityName = localStorage.getItem("municipalityName") ?? "";

const EMPTY_FORM: FormData = {
  login_user_name: "", admin_name: "", municipality_name: municipalityName,
  password: "", email: "", role_id: "", ward_id: "", municipality_id: MunicipalityID,
};

// ─── Multi-Select Ward Tag Input ───────────────────────────────────────────────
const WardTagSelect: React.FC<{
  selectedIds: string[];
  options: { label: string; value: string }[];
  placeholder: string;
  disabled?: boolean;
  onChange: (values: string[]) => void;
}> = ({ selectedIds, options, placeholder, disabled, onChange }) => {
  const [search, setSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const unselected = options.filter((o) => !selectedIds.includes(o.value));
  const filtered = search.trim()
    ? unselected.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : unselected;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addWard = (value: string) => {
    if (!selectedIds.includes(value)) {
      onChange([...selectedIds, value]);
    }
    setSearch("");
    inputRef.current?.focus();
  };

  const removeWard = (value: string) => {
    onChange(selectedIds.filter((id) => id !== value));
  };

  const selectedLabels = selectedIds.map((id) => ({
    value: id,
    label: options.find((o) => o.value === id)?.label ?? id,
  }));

  return (
    <div ref={ref} className="w-full">
      {/* Tag + Input box */}
      <div
        onClick={() => { if (!disabled) { setShowSuggestions(true); inputRef.current?.focus(); } }}
        className={`min-h-[42px] flex flex-wrap gap-1.5 items-center border rounded-md px-2 py-1.5 cursor-text transition-all
          ${disabled
            ? "bg-gray-100 border-gray-200 cursor-not-allowed"
            : showSuggestions
            ? "border-blue-400 ring-2 ring-blue-100 bg-white"
            : "border-gray-300 bg-white hover:border-gray-400"
          }`}
      >
        {/* Tags */}
        {selectedLabels.map((item) => (
          <span
            key={item.value}
            className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full"
          >
            {item.label}
            {!disabled && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); removeWard(item.value); }}
                className="ml-0.5 w-3.5 h-3.5 rounded-full bg-blue-200 hover:bg-blue-400 hover:text-white text-blue-600 flex items-center justify-center flex-shrink-0 transition-colors"
                title={`Remove ${item.label}`}
              >
                <svg viewBox="0 0 10 10" className="w-2 h-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M2 2l6 6M8 2l-6 6" />
                </svg>
              </button>
            )}
          </span>
        ))}

        {/* Search input */}
        {!disabled && (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            placeholder={selectedIds.length === 0 ? placeholder : "Add more..."}
            className="flex-1 min-w-[120px] text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400 py-0.5"
          />
        )}

        {disabled && selectedIds.length === 0 && (
          <span className="text-sm text-gray-400 px-1">{placeholder}</span>
        )}
      </div>

      {/* Dropdown */}
      {showSuggestions && !disabled && (
        <div className="relative">
          <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {filtered.length > 0 ? (
              filtered.map((o) => (
                <li
                  key={o.value}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addWard(o.value)}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 text-gray-700 hover:text-blue-700 transition-colors"
                >
                  {o.label}
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-sm text-gray-400">
                {search.trim() ? "No results found" : unselected.length === 0 ? "All wards selected" : "Type to search wards..."}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const AddCollector = () => {
  const [collectorType, setCollectorType] = useState<CollectorType>("");
  const [boroughs, setBoroughs] = useState<Borough[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [roleTypes, setRoleTypes] = useState<RoleType[]>([]);
  const [selectedBorough, setSelectedBorough] = useState<string>("");
  const [selectedWards, setSelectedWards] = useState<string[]>([]); // ← now array
  const [loadingBorough, setLoadingBorough] = useState(false);
  const [loadingWard, setLoadingWard] = useState(false);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = React.useRef(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const MunicipalityID = localStorage.getItem("municipalityId");
  const municipalityName = localStorage.getItem("municipalityName") ?? "";

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailValid = emailRegex.test(form.email);
  const passwordChecks = {
    length: form.password.length >= 6,
    uppercase: /[A-Z]/.test(form.password),
    number: /\d/.test(form.password),
  };
  const passwordValid = Object.values(passwordChecks).every(Boolean);

  const showForm =
    collectorType === "counter" ||
    (collectorType === "field" && selectedBorough !== "" && selectedWards.length > 0);

  useEffect(() => {
    axiosInstance.get(API_URLS.roleType, { params: { role_type: "collector" } })
      .then(res => setRoleTypes(res.data?.data || []))
      .catch((err: any) => {
        const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to load role types";
        toast.error(msg, { position: "top-right", autoClose: 3000 });
      });
  }, []);

  useEffect(() => {
    if (collectorType !== "field") return;
    setLoadingBorough(true);
    axiosInstance.post(API_URLS.GetMunicipalityBorough, {
      municipalityTbl_id: MunicipalityID, page: 1, limit: 500, surveyer_id: 0,
    })
      .then(res => setBoroughs(res.data?.data?.data || []))
      .catch((err: any) => {
        const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to load boroughs";
        toast.error(msg, { position: "top-right", autoClose: 3000 });
      })
      .finally(() => setLoadingBorough(false));
  }, [collectorType]);

  useEffect(() => {
    if (!selectedBorough) return;
    setSelectedWards([]);
    setWards([]);
    setLoadingWard(true);
    axiosInstance.post(API_URLS.GetBoroughWiseWard, {
      municipalityTbl_id: MunicipalityID, borough_primary_id: selectedBorough,
      page: 1, limit: 50, surveyer_id: 0,
    })
      .then(res => {
        const wardList = res.data?.data?.data?.map((w: any) => ({
          id: w.ward_primary_id, name: w.ward_no,
        })) || [];
        setWards(wardList);
      })
      .catch((err: any) => {
        const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to load wards";
        toast.error(msg, { position: "top-right", autoClose: 3000 });
      })
      .finally(() => setLoadingWard(false));
  }, [selectedBorough]);

  const handleTypeChange = (type: CollectorType) => {
    const matchedRole = roleTypes.find(r =>
      type === "counter" ? r.role_name === "counter-collector" : r.role_name === "collector"
    );
    setCollectorType(type);
    setSelectedBorough("");
    setSelectedWards([]);
    setWards([]);
    setBoroughs([]);
    setForm({ ...EMPTY_FORM, role_id: matchedRole ? String(matchedRole.primary_role_id) : "" });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.name === "email") setEmailTouched(true);
    if (e.target.name === "password") setPasswordTouched(true);
  };

  const nav = useNavigate();
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    const requiredFields: { key: keyof FormData; label: string }[] = [
      { key: "admin_name", label: "Full Name" },
      { key: "login_user_name", label: "Username" },
      { key: "password", label: "Password" },
    ];
    for (const { key, label } of requiredFields) {
      if (!String(form[key] ?? "").trim()) {
        toast.warn(`Please fill in ${label}`, { position: "top-right", autoClose: 3000 });
        return;
      }
    }
    if (form.email && !emailValid) { setEmailTouched(true); return; }
    if (!passwordValid) { setPasswordTouched(true); return; }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      // Send comma-joined ward ids (or array depending on your API)
      const payload = { ...form, ward_id: selectedWards.map(id => Number(id)) };
            payload.municipality_name = municipalityName;
            payload.municipality_id = MunicipalityID
      await axiosInstance.post(API_URLS.collectorcreate, payload);
      toast.success(`${collectorType === "counter" ? "Counter" : "Field"} Collector added successfully!`, { position: "top-right", autoClose: 3000 });
      setCollectorType("");
      setSelectedBorough("");
      setSelectedWards([]);
      setBoroughs([]);
      setWards([]);
      setEmailTouched(false);
      setPasswordTouched(false);
      setShowPassword(false);
      setForm(EMPTY_FORM);
      setTimeout(() => { nav('/dashboard/view-collector'); }, 1000);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Something went wrong.";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    } finally {
      setTimeout(() => { isSubmittingRef.current = false; setIsSubmitting(false); }, 3000);
    }
  };

  const fields: { name: keyof FormData; max: number; min: number; label: string; type?: string; placeholder: string; required?: boolean }[] = [
    { name: "admin_name", max: 100, min: 2, label: "Full Name", placeholder: "Rahul Kumar", required: true },
    { name: "login_user_name", max: 100, min: 2, label: "Username", placeholder: "rahul_tmr", required: true },
    { name: "email", max: 100, min: 2, label: "Email", type: "email", placeholder: "rahul@gmail.com", required: false },
    { name: "password", max: 20, min: 6, label: "Password", type: "password", placeholder: "Min 6 chars, 1 uppercase, 1 number", required: true },
  ];

  const collectorOptions = roleTypes.map((r) => ({
    label: r.role_name === "counter-collector" ? "Counter Collector" : "Field Collector",
    value: r.role_name === "counter-collector" ? "counter" : "field",
  }));

  const boroughOptions = boroughs.map((b) => ({
    label: b.borough_name,
    value: String(b.borough_primary_id),
  }));

  const wardOptions = wards.map((w) => ({
    label: w.name,
    value: String(w.id),
  }));

  return (
    <div className="bg-gray-100 min-h-screen p-6">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Add Collector</h1>
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Collector Type */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Collector Type</label>
            <CustomSelect
              name="collector_type"
              value={collectorType}
              options={collectorOptions}
              placeholder="Select Collector Type"
              onChange={(e: any) => handleTypeChange(e.target.value)}
            />
          </div>

          {/* Borough */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Borough</label>
            <CustomSelect
              name="borough"
              value={selectedBorough}
              options={boroughOptions}
              placeholder={collectorType !== "field" ? "— Select —" : loadingBorough ? "Loading..." : "Select Borough"}
              disabled={collectorType !== "field" || loadingBorough}
              onChange={(e: any) => setSelectedBorough(e.target.value)}
            />
          </div>

          {/* Ward — Multi Tag Select */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ward
              {selectedWards.length > 0 && (
                <span className="ml-2 text-xs font-normal text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                  {selectedWards.length} selected
                </span>
              )}
            </label>
            <WardTagSelect
              selectedIds={selectedWards}
              options={wardOptions}
              placeholder={
                collectorType !== "field"
                  ? "Type to search ward..."
                  : selectedBorough === ""
                  ? "Select borough first..."
                  : loadingWard
                  ? "Loading..."
                  : "Type to search ward..."
              }
              disabled={collectorType !== "field" || selectedBorough === "" || loadingWard}
              onChange={setSelectedWards}
            />
          </div>
        </div>

        {/* Hint messages */}
        {collectorType === "field" && selectedBorough === "" && (
          <p className="text-xs text-orange-500 mt-3">👆 Select a Borough to continue</p>
        )}
        {collectorType === "field" && selectedBorough !== "" && selectedWards.length === 0 && (
          <p className="text-xs text-orange-500 mt-3">👆 Type ward name to search and select (you can select multiple)</p>
        )}
      </div>

      {showForm && (
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="mb-6">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                collectorType === "counter" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
              }`}>
                {collectorType === "counter" ? "Counter Collector" : "Field Collector"}
              </span>
            </div>

            {collectorType === "field" && selectedWards.length > 0 && (
              <div className="mt-3 flex items-start gap-2 flex-wrap">
                <span className="text-xs font-medium text-gray-500 mt-1 whitespace-nowrap">Ward:</span>
                <div className="flex flex-wrap gap-1.5">
                  {selectedWards.map((id) => (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 bg-gray-100 border border-gray-300 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full"
                    >
                      {wards.find(w => String(w.id) === id)?.name ?? id}
                      <button
                        type="button"
                        onClick={() => setSelectedWards(prev => prev.filter(w => w !== id))}
                        className="ml-0.5 w-3.5 h-3.5 rounded-full bg-gray-300 hover:bg-red-400 hover:text-white text-gray-500 flex items-center justify-center flex-shrink-0 transition-colors"
                        title="Remove ward"
                      >
                        <svg viewBox="0 0 10 10" className="w-2 h-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M2 2l6 6M8 2l-6 6" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {fields.map(f => (
              <div key={f.name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {f.label} {f.required && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input
                    type={f.name === "password" ? (showPassword ? "text" : "password") : (f.type || "text")}
                    name={f.name}
                    value={form[f.name]}
                    onChange={handleChange}
                    onBlur={() => {
                      if (f.name === "email") setEmailTouched(true);
                      if (f.name === "password") setPasswordTouched(true);
                    }}
                    placeholder={f.placeholder}
                    autoComplete="one-time-code"
                    className={`w-full border rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                      f.name === "email" && emailTouched && form.email
                        ? emailValid ? "border-green-400 focus:ring-green-400" : "border-red-400 focus:ring-red-400"
                        : f.name === "password" && passwordTouched && form.password
                        ? passwordValid ? "border-green-400 focus:ring-green-400" : "border-red-400 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-400"
                    }`}
                    maxLength={f.max || undefined}
                    minLength={f.min || undefined}
                  />
                  {f.name === "password" && (
                    <button type="button" onClick={() => setShowPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword
                        ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" /></svg>
                        : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      }
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {emailTouched && form.email && !emailValid && (
            <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Please enter a valid email address
            </p>
          )}

          {passwordTouched && form.password && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs font-medium text-gray-600 mb-2">Password requirements:</p>
              <div className="flex flex-col gap-1">
                {[
                  { key: "length", label: "At least 6 characters" },
                  { key: "uppercase", label: "At least 1 uppercase letter (A-Z)" },
                  { key: "number", label: "At least 1 number (0-9)" },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                      passwordChecks[key as keyof typeof passwordChecks] ? "bg-green-500" : "bg-gray-300"
                    }`}>
                      {passwordChecks[key as keyof typeof passwordChecks] && (
                        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
                          <path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </span>
                    <span className={`text-xs ${passwordChecks[key as keyof typeof passwordChecks] ? "text-green-600" : "text-gray-500"}`}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            <button type="button" onClick={() => handleTypeChange("")}
              className="px-6 py-3 rounded-lg font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} disabled={isSubmitting}
              className="flex-1 py-3 rounded-lg font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-400 bg-blue-600 hover:bg-blue-700">
              {isSubmitting ? "Saving..." : `Add ${collectorType === "counter" ? "Counter" : "Field"} Collector`}
            </button>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default AddCollector;